import { useMemo } from "react";
import {
  differenceInCalendarDays,
  format,
  isSameDay,
  isWithinInterval,
  startOfDay,
} from "date-fns";
import { cn } from "@/lib/utils";
import type { TaskRead, TaskStatusConfig } from "@/api/types";

interface TimelineViewProps {
  /** Already filtered by the page (priority / status / assignee). */
  tasks: TaskRead[];
  startDate: Date;
  endDate: Date;
  /** Show the task's project in the tooltip (My Tasks cross-project feed). */
  showProject: boolean;
  getStatusConfig: (
    key: string | null | undefined,
  ) => TaskStatusConfig | null;
  onOpenDetail: (task: TaskRead) => void;
}

/** Status-config color name → bar fill. Literal classes so Tailwind keeps
 *  them; unknown colors fall back to slate. */
const BAR_COLORS: Record<string, string> = {
  gray: "bg-slate-400",
  slate: "bg-slate-400",
  blue: "bg-blue-400",
  sky: "bg-sky-400",
  cyan: "bg-cyan-400",
  teal: "bg-teal-400",
  indigo: "bg-indigo-400",
  violet: "bg-violet-400",
  purple: "bg-violet-400",
  amber: "bg-amber-400",
  yellow: "bg-amber-400",
  orange: "bg-orange-400",
  emerald: "bg-emerald-400",
  green: "bg-emerald-400",
  rose: "bg-rose-400",
  red: "bg-rose-400",
};

interface Bar {
  start: Date;
  end: Date;
}

/** A task's [start, end] span. `start_date` is read defensively — the
 *  backend doesn't ship it yet, so today this resolves to a 1-day bar on
 *  due_at. Once start_date lands, multi-day bars work with no FE change. */
function barFor(task: TaskRead): Bar | null {
  const startRaw =
    (task as { start_date?: string | null }).start_date ?? null;
  const start = startRaw ? new Date(startRaw) : null;
  const due = task.due_at ? new Date(task.due_at) : null;
  const a = start ?? due;
  const b = due ?? start;
  if (!a || !b || Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) {
    return null;
  }
  // Guard against a due date before the start date.
  return a <= b
    ? { start: startOfDay(a), end: startOfDay(b) }
    : { start: startOfDay(b), end: startOfDay(a) };
}

interface Lane {
  assigneeId: string;
  assignee: string;
  rows: { task: TaskRead; bar: Bar }[];
}

export function TimelineView({
  tasks,
  startDate,
  endDate,
  showProject,
  getStatusConfig,
  onOpenDetail,
}: TimelineViewProps) {
  const rangeStart = startOfDay(startDate);
  const rangeEnd = startOfDay(endDate);
  // Inclusive day count across the range.
  const totalDays = Math.max(
    1,
    differenceInCalendarDays(rangeEnd, rangeStart) + 1,
  );

  const days = useMemo(() => {
    const out: Date[] = [];
    for (let i = 0; i < totalDays; i++) {
      const d = new Date(rangeStart);
      d.setDate(d.getDate() + i);
      out.push(d);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeStart.getTime(), totalDays]);

  // Group in-range tasks into assignee lanes.
  const lanes = useMemo<Lane[]>(() => {
    const byAssignee = new Map<string, Lane>();
    for (const task of tasks) {
      const bar = barFor(task);
      if (!bar) continue;
      // Keep only bars overlapping the visible range.
      if (bar.end < rangeStart || bar.start > rangeEnd) continue;
      const id = task.assigned_to_id;
      let lane = byAssignee.get(id);
      if (!lane) {
        lane = {
          assigneeId: id,
          assignee: task.assigned_to_name || "Unassigned",
          rows: [],
        };
        byAssignee.set(id, lane);
      }
      lane.rows.push({ task, bar });
    }
    const list = [...byAssignee.values()];
    list.sort((a, b) => a.assignee.localeCompare(b.assignee));
    for (const lane of list) {
      lane.rows.sort(
        (a, b) => a.bar.start.getTime() - b.bar.start.getTime(),
      );
    }
    return list;
  }, [tasks, rangeStart, rangeEnd]);

  const today = new Date();
  const todayInRange = isWithinInterval(startOfDay(today), {
    start: rangeStart,
    end: rangeEnd,
  });
  const todayLeftPct =
    (differenceInCalendarDays(startOfDay(today), rangeStart) / totalDays) *
    100;

  function geometry(bar: Bar) {
    // Clip the bar to the visible window so a span extending beyond either
    // edge isn't counted as extra width (lanes only contain overlapping
    // bars, so visStart <= visEnd holds).
    const visStart = bar.start < rangeStart ? rangeStart : bar.start;
    const visEnd = bar.end > rangeEnd ? rangeEnd : bar.end;
    const offset = differenceInCalendarDays(visStart, rangeStart);
    const span = differenceInCalendarDays(visEnd, visStart) + 1;
    const leftPct = (offset / totalDays) * 100;
    const widthPct = (span / totalDays) * 100;
    return { leftPct, widthPct };
  }

  return (
    <div className="overflow-hidden rounded-plane bg-white shadow-plane">
      {/* Day header */}
      <div className="flex border-b border-slate-900/[0.06] bg-slate-50">
        <div className="w-44 shrink-0 border-r border-slate-200 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          Assignee
        </div>
        <div className="flex flex-1">
          {days.map((day, i) => {
            const weekend = day.getDay() === 0 || day.getDay() === 6;
            const isToday = isSameDay(day, today);
            return (
              <div
                key={i}
                className={cn(
                  "flex-1 border-r border-slate-900/[0.06] py-1.5 text-center text-[10px] last:border-r-0",
                  weekend && "bg-slate-100/70",
                  isToday && "bg-indigo-50 font-semibold text-indigo-600",
                )}
              >
                <div className="text-slate-400">{format(day, "EEE")}</div>
                <div className="font-medium text-slate-600">
                  {format(day, "d")}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Lanes */}
      {lanes.length === 0 ? (
        <div className="px-4 py-12 text-center text-sm text-slate-400">
          No tasks in this period.
        </div>
      ) : (
        lanes.map((lane) => (
          <div
            key={lane.assigneeId}
            className="flex border-b border-slate-900/[0.06] last:border-b-0"
          >
            <div className="flex w-44 shrink-0 items-center border-r border-slate-200 bg-slate-50/40 px-3 py-3 text-sm font-medium text-slate-700">
              <span className="truncate">{lane.assignee}</span>
            </div>
            <div className="relative min-h-[52px] flex-1">
              {/* Day grid lines */}
              <div className="absolute inset-0 flex">
                {days.map((day, i) => {
                  const weekend = day.getDay() === 0 || day.getDay() === 6;
                  return (
                    <div
                      key={i}
                      className={cn(
                        "flex-1 border-r border-slate-900/[0.06] last:border-r-0",
                        weekend && "bg-slate-50/70",
                      )}
                    />
                  );
                })}
              </div>
              {/* Today marker */}
              {todayInRange && (
                <div
                  className="absolute bottom-0 top-0 z-10 w-px bg-rose-400"
                  style={{ left: `${todayLeftPct}%` }}
                  aria-hidden
                />
              )}
              {/* Task bars — one row per task */}
              <div className="relative z-[5] space-y-1 px-0.5 py-2">
                {lane.rows.map(({ task, bar }) => {
                  const { leftPct, widthPct } = geometry(bar);
                  const cfg = getStatusConfig(task.status);
                  const projectName = (
                    task as { project_name?: string }
                  ).project_name;
                  return (
                    <div
                      key={task.id}
                      className="group relative h-7"
                      style={{
                        marginLeft: `${leftPct}%`,
                        width: `${widthPct}%`,
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => onOpenDetail(task)}
                        className={cn(
                          "absolute inset-0 flex items-center overflow-hidden rounded-md px-2 text-left transition-all hover:ring-2 hover:ring-indigo-300",
                          BAR_COLORS[cfg?.color ?? ""] ?? "bg-slate-400",
                        )}
                      >
                        <span className="truncate text-xs font-medium text-white">
                          {task.title}
                        </span>
                      </button>
                      {/* Hover tooltip */}
                      <div className="pointer-events-none absolute bottom-full left-0 z-20 mb-1 hidden whitespace-nowrap rounded-lg bg-slate-900 p-2 text-xs text-white shadow-lg group-hover:block">
                        <p className="font-medium">{task.title}</p>
                        {showProject && projectName && (
                          <p className="text-indigo-200">{projectName}</p>
                        )}
                        {task.company_name && (
                          <p className="text-slate-300">{task.company_name}</p>
                        )}
                        <p className="text-slate-400">
                          {cfg?.label ?? task.status}
                          {task.due_at &&
                            ` · due ${format(new Date(task.due_at), "d MMM")}`}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
